/**
 * Code typography components with Nothing-inspired monospace styling
 */

import React from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Text, TextProps } from '../Text';
import { useTheme } from '../../themes';
import { colorTokens } from '../../tokens';

export interface CodeProps extends Omit<TextProps, 'variant'> {
  children: React.ReactNode;
}

export interface CodeBlockProps extends CodeProps {
  language?: string;
  showLineNumbers?: boolean;
  scrollable?: boolean;
}

/**
 * Inline code component for small code snippets
 */
export function Code({ children, style, ...props }: CodeProps) {
  const { theme } = useTheme();

  const codeStyle = [
    styles.inlineCode,
    {
      backgroundColor:
        theme.colors.surface?.secondary || theme.colors.background.secondary,
      color: theme.colors.text.primary,
      paddingHorizontal: theme.spacing.spacing[1],
      paddingVertical: theme.spacing.spacing[1] / 2,
      borderRadius: theme.spacing.spacing[1],
    },
    style,
  ];

  return (
    <Text variant='code' style={codeStyle} selectable={true} {...props}>
      {children}
    </Text>
  );
}

/**
 * Code block component for larger code snippets
 */
export function CodeBlock({
  children,
  language,
  showLineNumbers = false,
  scrollable = true,
  style,
  ...props
}: CodeBlockProps) {
  const { theme } = useTheme();

  const blockStyle = [
    styles.codeBlock,
    {
      backgroundColor:
        theme.colors.surface?.secondary || theme.colors.background.secondary,
      borderColor: theme.colors.border.primary,
      borderRadius: theme.spacing.spacing[2],
      padding: theme.spacing.spacing[4],
      marginVertical: theme.spacing.spacing[2],
    },
    style,
  ];

  const content = (
    <Text variant='codeBlock' color='primary' selectable={true} {...props}>
      {children}
    </Text>
  );

  return (
    <View style={blockStyle}>
      {language && (
        <Text variant='caption' color='tertiary' style={styles.languageLabel}>
          {language.toUpperCase()}
        </Text>
      )}
      {scrollable ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {content}
        </ScrollView>
      ) : (
        content
      )}
    </View>
  );
}

/**
 * Keyboard key component for showing keyboard shortcuts
 */
export function Kbd({ children, style, ...props }: CodeProps) {
  const { theme } = useTheme();

  const kbdStyle = [
    styles.kbd,
    {
      backgroundColor: theme.colors.background.primary,
      borderColor: theme.colors.border.primary,
      color: theme.colors.text.primary,
      paddingHorizontal: theme.spacing.spacing[2],
      paddingVertical: theme.spacing.spacing[1],
      borderRadius: theme.spacing.spacing[1],
      borderWidth: 1,
      shadowColor: theme.colors.border.primary,
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.3,
      shadowRadius: 1,
      elevation: 1,
    },
    style,
  ];

  return (
    <Text variant='code' style={kbdStyle} {...props}>
      {children}
    </Text>
  );
}

/**
 * Variable name component for technical documentation
 */
export function Var({ children, style, ...props }: CodeProps) {
  const { theme } = useTheme();

  const varStyle = [
    styles.variable,
    {
      color: colorTokens.semantic.info.DEFAULT,
      fontStyle: 'italic',
    },
    style,
  ];

  return (
    <Text variant='code' style={varStyle} {...props}>
      {children}
    </Text>
  );
}

/**
 * Sample output component for showing command results
 */
export function Samp({ children, style, ...props }: CodeProps) {
  const { theme } = useTheme();

  const sampStyle = [
    styles.sample,
    {
      backgroundColor: theme.colors.background.tertiary,
      color: theme.colors.text.secondary,
      paddingHorizontal: theme.spacing.spacing[2],
      paddingVertical: theme.spacing.spacing[1],
      borderRadius: theme.spacing.spacing[1],
    },
    style,
  ];

  return (
    <Text variant='code' style={sampStyle} {...props}>
      {children}
    </Text>
  );
}

const styles = StyleSheet.create({
  inlineCode: {
    // Inline code specific styles
  },
  codeBlock: {
    borderWidth: 1,
  },
  languageLabel: {
    position: 'absolute',
    top: 8,
    right: 12,
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 0.5,
  },
  kbd: {
    // Keyboard key specific styles
    minWidth: 24,
    textAlign: 'center',
  },
  variable: {
    // Variable name specific styles
  },
  sample: {
    // Sample output specific styles
  },
});
